<template>
    <div>
        <EditorSelector ref="selector" v-slot="{ selectedBookpart }">
            <div
                ref="editor"
                :class="[
                    'editor',
                    {
                        'editor--reviewer': isReviewer,
                        'editor--embed': isEmbed,
                        'editor--fullheight': isEmbed && !orderControlVisible,
                    },
                ]"
            >
                <Spinner v-if="!book || !book.id" class="spinner--opaque" />

                <EditorNavigation
                    v-if="!isProofreadingReviewer"
                    ref="navbar"
                    class="editor__navigation"
                    @change="fetchInitialBookparts"
                    @collapsed="onEditorNavigationCollapsed"
                    @styleSheetItemClick="onStyleSheetItemClick"
                />

                <EditorText
                    ref="EditorText"
                    :pages="pages"
                    :playing-part="playingPart"
                    @search="onEditorTextSearch"
                    @playPart="onPlayPart"
                    @toPage="handlePageNavigation"
                >
                    <template v-if="isProofreadingReviewer" #reviewer-navigation>
                        <EditorNavigation
                            ref="navbar"
                            reviewer-rail
                            class="editor__navigation"
                            @change="fetchInitialBookparts"
                            @collapsed="onEditorNavigationCollapsed"
                            @styleSheetItemClick="onStyleSheetItemClick"
                        />
                    </template>
                    <template #bottom>
                        <div class="editor__player editor__player--new">
                            <div v-if="pages.length" class="editor__player-inner">
                                <CharsInfo v-if="book.id" :book="book.id" />
                                <div class="editor__player-player">
                                    <PartsPlayer
                                        ref="partsPlayer"
                                        :total-pages="totalPages"
                                        @playingPartChanged="onPlayingPartChanged"
                                        @playingNextPart="onPlayingNextPart"
                                    >
                                        <template #after-player>
                                            <BooksBuilds
                                                v-if="!isEmbed && book.id"
                                                :book-id="book.id"
                                                checking-parts
                                                @openBrokenEditor="redirectToBrokenParts"
                                            />
                                        </template>
                                    </PartsPlayer>
                                </div>
                            </div>
                            <transition name="fade" appear>
                                <TrialBanner
                                    v-if="
                                        !isEmbed &&
                                        book.id &&
                                        ((book.limit_info && book.limit_info.is_trial) ||
                                            !book.limit) &&
                                        pages.length
                                    "
                                    @click.prevent="openBuyModal"
                                />
                            </transition>
                        </div>
                    </template>
                </EditorText>

                <button
                    v-if="selectedBookpart"
                    class="editor__toolbar__toggle"
                    :class="{ close: showToolbar }"
                    @click="toggleToolbar"
                >
                    {{ showToolbar ? '&gt;' : '&lt;' }}
                </button>

                <EditorToolbar
                    v-if="book.id && !isReviewer"
                    ref="editorToolbarRes"
                    :part="selectedBookpart"
                    :class="{ show: showToolbar }"
                    @play="stopPartsPlayer"
                    @massChange="
                        fetchInitialBookparts({ page: $route.query.page, preserveScroll: true })
                    "
                />
            </div>
        </EditorSelector>

        <portal to="admin-reindex">
            <AdminReindexButton v-if="$store.getters.isAdmin" @reindexed="onReindexed" />
        </portal>

        <portal to="header-after-title">
            <div style="display: flex; align-items: center">
                <BookStatusIcon />
                <BookReload
                    v-if="isBookTrial"
                    style="margin-left: 10px"
                    @reloaded="fetchInitialBookparts({ page: $route.query.page })"
                />
                <VDropdown
                    v-if="book.id && isBookTrial && pages.length"
                    :offset="20"
                    :open="openTrialPopover"
                    @hide="openTrialPopover = false"
                >
                    <button
                        type="button"
                        class="button button--trial"
                        @click.prevent="openTrialPopover = true"
                        @mouseover="onMouseOver"
                    >
                        {{ $t('misc.trialVersion') }}
                    </button>
                    <template #popper>
                        <TrialBanner class="trial-banner--header" @click.prevent="openBuyModal" />
                    </template>
                </VDropdown>
            </div>
        </portal>

        <portal to="modals">
            <Payment
                v-if="showPaymentModal"
                @buyed="onReindexed"
                @close="showPaymentModal = false"
            />
        </portal>
    </div>
</template>

<script>
import EditorNavigation from '../EditorNavigation/EditorNavigation';
import EditorText from '../EditorText/EditorText';
import EditorToolbar from '../EditorToolbar/EditorToolbar';
import EditorSelector from '../EditorSelector/EditorSelector';
import AdminReindexButton from '../AdminReindexButton/AdminReindexButton';
import BookStatusIcon from '../BookStatusIcon/BookStatusIcon';
import { BookReload } from '@speechki-frontend/core/book/components';
import { TrialBanner } from '@speechki-frontend/core/payment/components';
import { useSwipe } from '@vueuse/core';

import { Dropdown } from 'floating-vue';

import { mapState, mapActions, mapGetters } from 'vuex';

import { Payment } from '@speechki-frontend/core/payment/components';

import PartsPlayer from '../PartsPlayer/PartsPlayer';
import BooksBuilds from '../BooksBuilds/BooksBuilds';
import CharsInfo from '../CharsInfo/CharsInfo.vue';

import { clickOutside } from '@speechki-frontend/shared/helpers/directives/clickOutside';

import { getEditProgress, saveEditProgress, writeEditProgress } from '../../services/editProgress';
import { handleBookLoadError } from './bookLoadError';

export default {
    name: 'Editor',
    components: {
        EditorNavigation,
        EditorText,
        EditorToolbar,
        EditorSelector,
        AdminReindexButton,
        TrialBanner,
        VDropdown: Dropdown,
        Payment,
        BookStatusIcon,
        BookReload,
        PartsPlayer,
        BooksBuilds,
        CharsInfo,
    },
    directives: {
        clickOutside,
    },
    inject: {
        isEmbed: {
            default: false,
        },
    },
    provide() {
        return {
            setActivePage: this.setActivePage,
            fetchInitialPartsSub: undefined,
        };
    },
    inheritAttrs: false,
    props: {
        orderControlVisible: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            openTrialPopover: false,
            showDownloadTooltip: false,
            playingPart: undefined,
            showPaymentModal: false,
            showExportModal: false,

            //
            textContainer: undefined,

            swipe: null,
            isForceShow: false,
        };
    },
    computed: {
        ...mapState({
            book: (state) => state.book.book,
            textTypes: (state) => state.settings.textTypes,
        }),
        ...mapGetters(['isBookTrial', 'isAdmin', 'isPartner', 'isReviewer']),
        //
        ...mapState('pagedBookparts', {
            pages: (state) => state.pages,
            loading: (state) => state.loading,
            activePage: (state) => state.activePage,
        }),
        ...mapGetters('pagedBookparts', [
            'totalPages',
            'canLoadNext',
            'canLoadPrev',
            'isPartInActivePage',
            'flatSentencesList',
        ]),
        isSwiping() {
            if (this.swipe === null) {
                return false;
            }
            return this.swipe.isSwiping;
        },
        showToolbar() {
            return this.isForceShow;
        },
        isProofreadingReviewer() {
            return this.isEmbed && this.isReviewer;
        },
    },
    watch: {
        '$route.query'() {
            saveEditProgress({
                bookId: this.book.id || this.$route.params.id,
                ...this.$route.query,
            });
        },
        activePage(page) {
            this.$router.replace({
                ...this.$route,
                query: {
                    ...this.$route.query,
                    page: page,
                },
            });
        },
        isSwiping() {
            if (this.isSwiping) {
                return;
            }

            if (Math.abs(this.swipe.lengthX) > 2 * Math.abs(this.swipe.lengthY)) {
                let direction = this.swipe.direction;

                if (direction === 'right' && this.isForceShow) {
                    this.isForceShow = false;
                }

                if (
                    direction === 'left' &&
                    !this.isForceShow &&
                    this.$refs.editor.__vue__.selectedBookpart !== null
                ) {
                    this.isForceShow = true;
                }
            }
        },
    },
    mounted() {
        window.onbeforeunload = () => {
            writeEditProgress();
        };
        this.textContainer = document.querySelector('.text__inner');

        if (this.isReviewer) {
            document.querySelector('.page').classList.add('page--reviewer');
            this.$refs.editor.classList.add('editor--reviewer');
        }

        // subsribe to initial parts fetching to manage scroll
        let scrollLock = 0;
        this.fetchInitialPartsSub = this.$store.subscribeAction({
            before: ({ payload = {} }) => {
                if (payload.preserveScroll) {
                    scrollLock = this.textContainer.scrollTop;
                }
            },
            after: ({ type, payload = {} }) => {
                if (payload.preserveScroll) {
                    this.textContainer.scrollTop = scrollLock;
                    return;
                }

                if (type === 'pagedBookparts/fetchInitialBookparts') {
                    this.scrollAfterInitialFetch(payload);
                }
            },
        });

        setTimeout(() => {
            this.showDownloadTooltip = !this.isEmbed;
        }, 700);

        this.swipe = useSwipe(this.$refs.editor, {
            threshold: 100,
        });
    },
    async created() {
        const { query, params } = this.$route;

        if (this.book && parseInt(params.id) !== this.book.id) {
            this.clearBook();
            this.clearBookparts();
        }

        if (!query || !query.page) {
            let lastState = getEditProgress(params.id);

            let query = {
                page: 1,
            };

            if (lastState) {
                query = lastState;
                delete query.bookId;
                delete query.timestamp;
            }

            await this.$router.replace({
                name: this.$route.name,
                params: { id: params.id },
                query,
            });
        }

        try {
            await this.fetchBook(params.id);
        } catch (err) {
            console.log(err);
            handleBookLoadError(err, {
                isGuestAccess: this.$store.getters.isGuestAccess,
                router: this.$router,
            });
        }

        if (!this.book.id) return;

        await this.fetchInitialBookparts({ page: this.$route.query.page });

        // No part was ever selected/restored for this book (eg. a brand new
        // upload, or a book that was never opened before): auto-select the
        // first sentence so the toolbar panel isn't left empty on entry.
        if (!this.$route.query.part && this.flatSentencesList.length) {
            this.$nextTick(() => {
                this.$refs.selector.select({ id: this.flatSentencesList[0] });
            });
        }

        if (this.$route.query && this.$route.query.payment) {
            this.showPaymentModal = true;
        }
    },
    beforeUnmount() {
        this.$store.commit('CLEAR_BOOK');
        writeEditProgress();
        this.clearBookparts();

        if (this.fetchInitialPartsSub) {
            this.fetchInitialPartsSub();
        }
    },
    methods: {
        ...mapActions(['fetchBook', 'fetchLimits', 'clearBook']),
        //
        ...mapActions('pagedBookparts', ['fetchInitialBookparts', 'clearBookparts']),

        onReindexed() {
            this.$router.push({
                name: 'editor',
                params: { id: this.$route.params.id },
                query: { page: 1 },
            });

            this.fetchInitialBookparts({ page: this.$route.query.page });

            this.fetchBook(this.$route.params.id);
        },

        openBuyModal() {
            this.openTrialPopover = false;
            this.showPaymentModal = true;
        },

        onMouseOver() {
            this.openTrialPopover = true;
        },

        closeDownloadTooltip() {
            this.showDownloadTooltip = false;
        },

        onPlayingPartChanged(part) {
            this.playingPart = part;
        },

        onPlayingNextPart(part) {
            const { type, partId } = part;
            if (type === 'audio') {
                this.$nextTick(() => {
                    this.$refs.EditorText.scrollToPart(partId);
                });
            }
        },

        onPlayPart(event) {
            if (this.$refs.partsPlayer) {
                this.$refs.partsPlayer.makePlaylist({
                    page: this.activePage,
                    partId: event.id,
                    playImmediately: true,
                });
            }
        },

        stopPartsPlayer() {
            if (this.$refs.partsPlayer) {
                this.$refs.partsPlayer.pause();
            }
        },

        // Professional Prooflistening (embedded outsourced reviewer) top-panel
        // search cycling only: if the target part is already on the
        // currently-loaded active page, skip fetchInitialBookparts entirely
        // (no refetch, no loadingInitial spinner flash) and select it
        // directly — replicating the "already in active page" branch of
        // scrollAfterInitialFetch below, which normally only runs *after* a
        // fetch completes. Every other case (non-reviewer editor, other
        // guest personas, or a reviewer search result on a different/
        // not-yet-loaded page) falls through to the exact same
        // fetchInitialBookparts call as before this change.
        onEditorTextSearch(event) {
            if (
                this.isProofreadingReviewer &&
                event &&
                event.id &&
                this.isPartInActivePage(event.id)
            ) {
                this.$nextTick(() => {
                    this.$refs.selector.select({ id: event.id });
                });
                return;
            }

            this.fetchInitialBookparts({ ...event, select: true });
        },

        scrollAfterInitialFetch({ id, select = false }) {
            this.playingPart = undefined;

            const { part } = this.$route.query;
            const target = id || part;

            if (target && this.isPartInActivePage(target)) {
                this.$nextTick(() => {
                    if (select) {
                        this.$refs.selector.select({ id });
                        return;
                    }

                    this.$refs.EditorText.scrollToPart(target);
                });
                return;
            }

            this.$nextTick(() => {
                this.$scrollTo(document.querySelector('.editor-text-page.is-active'), 250, {
                    container: this.textContainer,
                    offset: -60,
                });
            });
        },

        // when no fetchInitialBookparts called, use this to set active page (eg pagination)
        // it is a copy of vuex action, but it works faster here and doesn't mess up scrolling
        async setActivePage(page) {
            page = Number(page);

            this.$store.commit('pagedBookparts/SET_ACTIVE_PAGE', page);

            if (page < this.totalPages && this.canLoadNext(page)) {
                await this.$store.dispatch('pagedBookparts/fetchNextPage', page);

                if (this.pages.length > 3) {
                    const scrollLock =
                        this.textContainer.scrollHeight - this.textContainer.scrollTop;

                    this.$store.commit('pagedBookparts/SHIFT_PAGE');

                    this.$nextTick(() => {
                        this.textContainer.scrollTop = this.textContainer.scrollHeight - scrollLock;
                    });
                }
            }

            if (page === this.pages[0].id && this.canLoadPrev(page)) {
                await this.$store.dispatch('pagedBookparts/fetchPrevPage', page);

                if (this.pages.length > 3) {
                    this.$store.commit('pagedBookparts/POP_PAGE');

                    this.$nextTick(() => {
                        const newPage = document.querySelector(
                            `[data-page="${this.activePage - 1}"]`
                        );

                        this.textContainer.scrollTop =
                            this.textContainer.scrollTop + newPage.offsetHeight;
                    });
                }
            }
        },

        async handlePageNavigation(page) {
            if (page > this.totalPages) {
                return;
            }

            const query = {
                ...this.$route.query,
                page,
            };

            if (query.part) {
                delete query.part;
            }

            this.$router.push({
                ...this.$route,
                query,
            });

            await this.fetchInitialBookparts({ page: +page });
        },

        redirectToBrokenParts() {
            this.$router.push({
                name: `${this.isEmbed ? 'embed-' : ''}broken-parts`,
                params: {
                    id: this.book.id,
                },
            });
        },

        onEditorNavigationCollapsed(value) {
            this.$refs.resizerRes.setLeftWidth(value ? 0 : this.leftSideWidth);
        },

        onStyleSheetItemClick(target) {
            const editorTextRefs = this.$refs.EditorText && this.$refs.EditorText.$refs;
            const textSearch =
                editorTextRefs && (editorTextRefs.textSearchRes || editorTextRefs.textSearchResPl);
            const searchSuggest = textSearch && textSearch.$refs.searchSuggestRes;

            if (!searchSuggest) return;

            searchSuggest.search = target;
        },

        toggleToolbar() {
            this.isForceShow = !this.isForceShow;
        },
    },
};
</script>

<style lang="scss" src="./Editor.scss"></style>
